document.documentElement.classList.remove('no-js');
document.documentElement.classList.add('js');

class SuprenxProductPage {
  constructor(root) {
    this.root = root;
    this.form = root.querySelector('[data-product-form]');
    this.variantInput = root.querySelector('[data-variant-id]');
    this.optionGroups = [...root.querySelectorAll('[data-product-option]')];
    this.price = root.querySelector('[data-product-price-current]');
    this.comparePrice = root.querySelector('[data-product-compare-price]');
    this.sku = root.querySelector('[data-product-sku]');
    this.skuRow = root.querySelector('[data-sku-row]');
    this.availability = root.querySelector('[data-product-availability]');
    this.addButton = root.querySelector('[name="add"]');
    this.addButtonLabel = root.querySelector('[data-add-button-label]');
    this.mediaItems = [...root.querySelectorAll('[data-media-item]')];
    this.mediaThumbs = [...root.querySelectorAll('[data-media-thumb]')];
    this.galleryLabel = root.querySelector('[data-gallery-label]');
    this.galleryStatus = root.querySelector('[data-gallery-status]');
    this.dialog = root.querySelector('[data-size-guide-dialog]');
    this.dialogOpener = null;
    this.pointerStartedOnBackdrop = false;
    this.variants = this.readVariants();

    this.prepareMedia();
    this.bindGallery();
    this.bindOptions();
    this.bindSizeGuide();
    this.sync(false);
  }

  readVariants() {
    const source = this.root.querySelector('[data-product-variants]');
    if (!source) return [];

    try {
      const variants = JSON.parse(source.textContent);
      return Array.isArray(variants) ? variants : [];
    } catch (error) {
      console.error('Unable to read product variants.', error);
      return [];
    }
  }

  normalizeColor(value = '') {
    return String(value).trim().toLocaleLowerCase().replace(/\s+/g, ' ');
  }

  colorFromAlt(alt = '') {
    const match = String(alt).match(/(?:^|[|;,])\s*color\s*:\s*([^|;,]+)/i)
      || String(alt).match(/\bcolor\s*:\s*([^|;,]+)/i);
    return match ? this.normalizeColor(match[1]) : '';
  }

  prepareMedia() {
    [...this.mediaItems, ...this.mediaThumbs].forEach((element) => {
      element.dataset.mediaColor = this.colorFromAlt(element.dataset.mediaAlt || '');
    });
  }

  bindGallery() {
    this.mediaThumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => {
        if (thumb.hidden) return;
        this.activateMedia(thumb.dataset.mediaId);
      });
    });
  }

  bindOptions() {
    this.optionGroups.forEach((group) => {
      group.querySelectorAll('[data-option-control]').forEach((control) => {
        control.addEventListener('change', () => this.sync(true));
      });
    });

    this.form?.addEventListener('submit', (event) => {
      const variant = this.findVariant(this.selectedOptions());
      if (!variant?.available) event.preventDefault();
    });
  }

  bindSizeGuide() {
    if (!this.dialog) return;

    const triggers = [...this.root.querySelectorAll('[data-size-guide-open]')];
    const closeButtons = [...this.dialog.querySelectorAll('[data-size-guide-close]')];
    const title = this.dialog.querySelector('[id$="-title"]');

    const open = (trigger) => {
      if (this.dialog.open) return;
      this.dialogOpener = trigger || document.activeElement;
      if (typeof this.dialog.showModal === 'function') {
        this.dialog.showModal();
      } else {
        this.dialog.setAttribute('open', '');
      }
      triggers.forEach((item) => item.setAttribute('aria-expanded', 'true'));
      document.body.classList.add('pdp-dialog-open');
      window.requestAnimationFrame(() => title?.focus({ preventScroll: true }));
    };

    const close = () => {
      if (typeof this.dialog.close === 'function') {
        this.dialog.close();
      } else {
        this.dialog.removeAttribute('open');
        this.dialog.dispatchEvent(new Event('close'));
      }
    };

    triggers.forEach((trigger) => {
      trigger.setAttribute('aria-expanded', 'false');
      trigger.addEventListener('click', () => open(trigger));
    });
    closeButtons.forEach((button) => button.addEventListener('click', close));

    this.dialog.addEventListener('pointerdown', (event) => {
      this.pointerStartedOnBackdrop = event.target === this.dialog;
    });
    this.dialog.addEventListener('pointerup', (event) => {
      if (this.pointerStartedOnBackdrop && event.target === this.dialog) close();
      this.pointerStartedOnBackdrop = false;
    });
    this.dialog.addEventListener('close', () => {
      triggers.forEach((item) => item.setAttribute('aria-expanded', 'false'));
      document.body.classList.remove('pdp-dialog-open');
      const target = this.dialogOpener?.isConnected ? this.dialogOpener : triggers[0];
      window.requestAnimationFrame(() => target?.focus({ preventScroll: true }));
    });
  }

  selectedOptions() {
    if (!this.optionGroups.length) return this.variants[0]?.options || [];
    const selected = [];
    this.optionGroups.forEach((group) => {
      const position = Number(group.dataset.optionPosition);
      const select = group.querySelector('select[data-option-control]');
      const checked = group.querySelector('input[data-option-control]:checked');
      selected[position] = select?.value ?? checked?.value ?? '';
    });
    return selected;
  }

  findVariant(selected) {
    return this.variants.find((variant) => variant.options.every((value, index) => value === selected[index]));
  }

  hasAvailableVariant(selected, position, value) {
    return this.variants.some((variant) => (
      variant.available
      && variant.options[position] === value
      && variant.options.every((optionValue, index) => (
        index >= position || !selected[index] || optionValue === selected[index]
      ))
    ));
  }

  updateOptionAvailability(selected) {
    this.optionGroups.forEach((group) => {
      const position = Number(group.dataset.optionPosition);
      const isColorOption = group.dataset.optionKind === 'color';
      const select = group.querySelector('select[data-option-control]');

      if (select) {
        [...select.options].forEach((option) => {
          const available = this.hasAvailableVariant(selected, position, option.value);
          option.disabled = isColorOption ? false : !available;
        });
        return;
      }

      group.querySelectorAll('input[data-option-control]').forEach((control) => {
        const available = this.hasAvailableVariant(selected, position, control.value);
        const choice = control.closest('[data-option-choice]');

        if (isColorOption) {
          control.disabled = false;
          choice?.classList.remove('is-unavailable');
          choice?.classList.toggle('is-sold-out', !available);
          choice?.removeAttribute('aria-disabled');
          const soldState = choice?.querySelector('[data-option-sold-state]');
          if (soldState) soldState.hidden = available;
          return;
        }

        control.disabled = !available;
        choice?.classList.remove('is-sold-out');
        choice?.classList.toggle('is-unavailable', !available);
        choice?.toggleAttribute('aria-disabled', !available);
      });
    });
  }

  updateOptionLabels() {
    this.optionGroups.forEach((group) => {
      const select = group.querySelector('select[data-option-control]');
      const checked = group.querySelector('input[data-option-control]:checked');
      const output = group.querySelector('[data-option-value-output]');
      if (output) output.textContent = select?.value ?? checked?.value ?? '';
    });
  }

  updateVariant(variant, updateUrl) {
    if (this.variantInput) this.variantInput.value = variant?.id || '';
    if (this.price) this.price.textContent = variant?.price || '—';

    if (this.comparePrice) {
      this.comparePrice.textContent = variant?.compareAtPrice || '';
      this.comparePrice.hidden = !variant?.compareAtPrice;
    }

    if (this.sku) this.sku.textContent = variant?.sku || '';
    if (this.skuRow) this.skuRow.hidden = !variant?.sku;

    const status = !variant ? 'unavailable' : variant.available ? 'available' : 'sold-out';
    const statusLabel = status === 'available' ? 'Ready to add' : status === 'sold-out' ? 'Sold out' : 'Unavailable';
    if (this.availability) {
      this.availability.dataset.status = status;
      const indicator = this.availability.querySelector('span');
      this.availability.textContent = statusLabel;
      if (indicator) this.availability.prepend(indicator);
    }

    if (this.addButton) {
      this.addButton.disabled = status !== 'available';
      const label = status === 'available'
        ? this.addButton.dataset.addLabel
        : status === 'sold-out'
          ? this.addButton.dataset.soldLabel
          : this.addButton.dataset.unavailableLabel;
      if (this.addButtonLabel) this.addButtonLabel.textContent = label || statusLabel;
    }

    if (variant && updateUrl) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
    }

    this.root.dispatchEvent(new CustomEvent('product:variant-change', {
      bubbles: true,
      detail: { variant: variant || null },
    }));
  }

  selectedColor() {
    const group = this.optionGroups.find((item) => item.dataset.optionKind === 'color');
    if (!group) return '';
    return group.querySelector('select[data-option-control]')?.value
      ?? group.querySelector('input[data-option-control]:checked')?.value
      ?? '';
  }

  syncGallery(colorName, featuredMediaId) {
    if (!this.mediaItems.length) return;

    const normalizedColor = this.normalizeColor(colorName);
    const matchingTaggedItems = this.mediaItems.filter((item) => (
      item.dataset.mediaColor && item.dataset.mediaColor === normalizedColor
    ));
    const shouldFilter = normalizedColor && matchingTaggedItems.length > 0;
    const visibleIds = new Set();

    this.mediaItems.forEach((item) => {
      const mediaColor = item.dataset.mediaColor;
      const visible = !shouldFilter || !mediaColor || mediaColor === normalizedColor;
      if (visible) visibleIds.add(item.dataset.mediaId);
    });
    this.mediaThumbs.forEach((thumb) => {
      thumb.hidden = !visibleIds.has(thumb.dataset.mediaId);
    });

    const requestedId = featuredMediaId ? String(featuredMediaId) : '';
    const currentItem = this.mediaItems.find((item) => !item.hidden && visibleIds.has(item.dataset.mediaId));
    const nextId = visibleIds.has(requestedId)
      ? requestedId
      : currentItem?.dataset.mediaId || [...visibleIds][0];

    this.activateMedia(nextId, visibleIds);
    if (this.galleryLabel) {
      this.galleryLabel.textContent = shouldFilter ? `${colorName} gallery`.toLocaleUpperCase() : 'PRODUCT GALLERY';
    }
  }

  activateMedia(mediaId, visibleIds = null) {
    if (!mediaId) return;
    const availableIds = visibleIds || new Set(
      this.mediaThumbs.filter((thumb) => !thumb.hidden).map((thumb) => thumb.dataset.mediaId),
    );

    this.mediaItems.forEach((item) => {
      const active = item.dataset.mediaId === String(mediaId);
      item.hidden = !active;
      if (!active) item.querySelectorAll('video').forEach((video) => video.pause());
    });
    this.mediaThumbs.forEach((thumb) => {
      const active = thumb.dataset.mediaId === String(mediaId);
      thumb.classList.toggle('is-active', active);
      if (active) thumb.setAttribute('aria-current', 'true');
      else thumb.removeAttribute('aria-current');
    });

    if (this.galleryStatus) {
      const orderedIds = this.mediaThumbs
        .filter((thumb) => availableIds.has(thumb.dataset.mediaId))
        .map((thumb) => thumb.dataset.mediaId);
      const activeIndex = Math.max(orderedIds.indexOf(String(mediaId)), 0) + 1;
      const total = orderedIds.length;
      this.galleryStatus.textContent = `${String(activeIndex).padStart(2, '0')} / ${String(total).padStart(2, '0')}`;
    }
  }

  sync(updateUrl) {
    if (!this.variants.length || !this.form) {
      this.syncGallery('', null);
      return;
    }

    const selected = this.selectedOptions();
    const variant = this.findVariant(selected);
    this.updateOptionAvailability(selected);
    this.updateOptionLabels();
    this.updateVariant(variant, updateUrl);
    this.syncGallery(this.selectedColor(), variant?.featuredMediaId);
  }
}

class SuprenxTheme {
  constructor() {
    this.bindProductSections();
    this.bindQuantityControls();
    this.bindDesktopMenus();
    this.bindMobileMenu();
    document.addEventListener('shopify:section:load', (event) => {
      this.bindProductSections(event.target);
      this.bindQuantityControls(event.target);
    });
  }

  bindProductSections(scope = document) {
    scope.querySelectorAll('[data-product-root]').forEach((root) => {
      if (root.dataset.productEnhanced === 'true') return;
      root.dataset.productEnhanced = 'true';
      new SuprenxProductPage(root);
    });
  }

  bindDesktopMenus() {
    const menus = [...document.querySelectorAll('[data-desktop-menu]')];
    let closeTimer;

    const closeAll = (except) => menus.forEach((menu) => {
      if (menu !== except) menu.removeAttribute('open');
    });

    menus.forEach((menu) => {
      menu.addEventListener('mouseenter', () => {
        if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
        window.clearTimeout(closeTimer);
        closeAll(menu);
        menu.setAttribute('open', '');
      });
      menu.addEventListener('mouseleave', () => {
        if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
        closeTimer = window.setTimeout(() => menu.removeAttribute('open'), 180);
      });
      menu.addEventListener('toggle', () => {
        if (menu.open) closeAll(menu);
      });
    });

    document.querySelector('[data-menu-scrim]')?.addEventListener('click', () => closeAll());
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') closeAll();
    });
  }

  bindQuantityControls(scope = document) {
    scope.querySelectorAll('[data-quantity]').forEach((control) => {
      if (control.dataset.quantityEnhanced === 'true') return;
      control.dataset.quantityEnhanced = 'true';
      const input = control.querySelector('input[type="number"]');
      if (!input) return;

      control.querySelectorAll('button').forEach((button) => {
        button.addEventListener('click', () => {
          const step = button.dataset.action === 'increase' ? 1 : -1;
          const next = Math.max(Number(input.min || 1), Number(input.value || 1) + step);
          input.value = next;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      });
    });
  }

  bindMobileMenu() {
    const drawer = document.querySelector('[data-mobile-menu]');
    if (!drawer) return;

    drawer.addEventListener('toggle', (event) => {
      if (event.target !== drawer) return;
      document.body.classList.toggle('menu-open', drawer.open);
      const label = drawer.querySelector('.mobile-menu-label');
      if (label) label.textContent = drawer.open ? 'CLOSE' : 'MENU';
    });

    drawer.querySelectorAll('.mobile-category-group').forEach((group) => {
      group.addEventListener('toggle', () => {
        if (!group.open) return;
        drawer.querySelectorAll('.mobile-category-group[open]').forEach((other) => {
          if (other !== group) other.removeAttribute('open');
        });
      });
    });

    drawer.querySelectorAll('a').forEach((link) => {
      link.addEventListener('click', () => drawer.removeAttribute('open'));
    });
  }
}

const initializeTheme = () => {
  if (document.documentElement.dataset.suprenxThemeReady === 'true') return;
  document.documentElement.dataset.suprenxThemeReady = 'true';
  new SuprenxTheme();
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeTheme, { once: true });
} else {
  initializeTheme();
}
